import { useMemo, useState } from 'react';

import { css } from '@compiled/react';

import { Box, media, Stack, xcss } from '@atlaskit/primitives';

import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { LoomAnimation } from '../components/loom-animation';
import { LoomOnboardingButton } from '../components/loom-onboarding-button';
import { LoomOnboardingHeading } from '../components/loom-onboarding-heading';
import { UseCaseButton } from '../components/use-case-button';
import { useOnboardingData } from '../utilities/save-data';
import { shouldAutoSelectLoomOnboardingWorkUseCase } from '../utilities/should-auto-select-loom-onboarding-work-use-case';
import { UseCaseOrder } from '../utilities/types';

import type { OnboardingStepProps, Role, UseCase } from '../utilities/types';

const gridStyles = xcss({
	display: 'grid',
	gap: 'space.500',
	gridTemplateColumns: 'minmax(0, 500px)',
	justifyContent: 'center',
	[media.above.md]: {
		gridTemplateColumns: 'repeat(2, minmax(0, 500px))',
	},
});

const loomColumnStyles = xcss({
	// Hide the persona animation on small screens (below 64rem / 1024px).
	display: 'none',
	flexDirection: 'column',
	alignItems: 'center',
	[media.above.md]: {
		display: 'flex',
	},
});

const gridPaddingCollapsedCss = css({
	paddingBlockEnd: '83px',
	'@media (min-width: 64em)': {
		paddingBlockEnd: '184px',
	},
});
const gridPaddingExpandedCss = css({
	paddingBlockEnd: '83px',
});

const sectionGap = 'space.1000';

const pageCss = css({
	minHeight: '100vh',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	paddingBlockStart: '144px',
	paddingBlockEnd: 'var(--ds-space-400, 32px)',
	paddingInlineStart: '100px',
	paddingInlineEnd: '100px',
	width: '100%',
	boxSizing: 'border-box',
});

const continueButtonWrapperCss = css({
	width: '200px',
});

const continueRowCss = css({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
});

const useCaseListCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
	width: '100%',
	padding: 0,
	margin: 0,
	listStyle: 'none',
	textAlign: 'left',
});

export const useShouldAutoSelectUseCasePlan = (): boolean => {
	const { email = '' } = useLoggedInUser();

	return useMemo(() => shouldAutoSelectLoomOnboardingWorkUseCase(email), [email]);
};

export const PersonaUseCaseStep = ({ onContinue }: OnboardingStepProps) => {
	const shouldAutoSelectWork = useShouldAutoSelectUseCasePlan();
	const { data, updateOnboardingData } = useOnboardingData();
	// Seed local state from whatever the user picked previously (e.g. they hit
	// "back" from the download step). Cached values always win over the
	// email-based auto-select fallback so a deliberate choice is never silently
	// overwritten when returning to this step.
	const [selectedUseCase, setSelectedUseCase] = useState<UseCase | undefined>(
		() => data.useCase ?? (shouldAutoSelectWork ? 'work' : undefined),
	);
	const [role, setRole] = useState<Role | undefined>(() => data.role);
	const isWorkExpanded = selectedUseCase === 'work';
	const isContinueEnabled =
		Boolean(selectedUseCase) && (selectedUseCase !== 'work' || Boolean(role));

	const setUseCase = (useCase: UseCase) => {
		setRole(undefined);
		setSelectedUseCase(useCase);
	};

	const handleContinue = () => {
		if (!isContinueEnabled || !selectedUseCase) {
			return;
		}
		updateOnboardingData({ useCase: selectedUseCase, role });
		onContinue({ persona: selectedUseCase });
	};

	return (
		<div css={pageCss}>
			<Stack space={sectionGap}>
				<LoomOnboardingHeading level={2}>How are you planning to use Loom?</LoomOnboardingHeading>

				<div
					css={[
						isWorkExpanded && gridPaddingExpandedCss,
						!isWorkExpanded && gridPaddingCollapsedCss,
					]}
				>
					<Box xcss={gridStyles}>
						<ul css={useCaseListCss} data-testid="loom-onboarding-use-case-buttons">
							{UseCaseOrder.map((useCase) => (
								<UseCaseButton
									key={useCase}
									useCase={useCase}
									isSelected={selectedUseCase === useCase}
									onSelect={setUseCase}
									role={role}
									onRoleChange={setRole}
								/>
							))}
						</ul>
						<Box xcss={loomColumnStyles}>
							<LoomAnimation />
						</Box>
					</Box>
				</div>
			</Stack>

			<div css={continueRowCss}>
				<div css={continueButtonWrapperCss}>
					<LoomOnboardingButton
						appearance={isContinueEnabled ? 'primary' : 'default'}
						onClick={handleContinue}
						disabled={!isContinueEnabled}
						fullWidth
					>
						Continue
					</LoomOnboardingButton>
				</div>
			</div>
		</div>
	);
};
